import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";

export interface ProjectListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: any[];
}

export interface CountsResponse {
  total_projects: number;
  urgent_projects: number;
  ongoing_projects: number;
}

export interface AgencyFilter {
  id: number;
  agency: string;
}

export interface DistrictFilter {
  district: string;
}

export interface PriorityFilter {
  key: string;
  label: string;
}

export interface StageFilter {
  key: string;
  label: string;
}

export interface MonitoringDateFilter {
  date: string;
}

@Injectable({
  providedIn: "root",
})
export class ProjectService {
  villageDetail: any;
  cala_name: string;
  individualVillageDetail: any;
  villageID   = new BehaviorSubject<any>(null);
  calaID      = new BehaviorSubject<any>(null);
  projectID   = new BehaviorSubject<any>(null);
  newVillageID = new BehaviorSubject<any>(null);

  serverUrl: string = environment.baseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get main project list with optional filters
   */
  getMainList(filters?: {
    page?: number;
    search?: string;
    agency?: string;
    district?: string;
    priority?: string;
    stage?: string;
    monitoring?: string;
    sort?: string;
  }): Observable<ProjectListResponse> {
    let params = new HttpParams();

    if (filters) {
      // ✅ PAGINATION FIX: page was commented out — now always sent so backend
      // returns the correct page instead of always returning page 1.
      if (filters.page != null) {
        params = params.set('page', filters.page.toString());
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.agency) {
        params = params.set('agency', filters.agency);
      }
      if (filters.district) {
        params = params.set('district', filters.district);
      }
      if (filters.priority) {
        params = params.set('priority', filters.priority);
      }
      if (filters.stage) {
        params = params.set('stage', filters.stage);
      }
      if (filters.monitoring) {
        params = params.set('monitoring', filters.monitoring);
      }
      if (filters.sort) {
        params = params.set('sort', filters.sort);
      }
    }

    return this.http.get<ProjectListResponse>(
      `${this.serverUrl}/api/project/v1/main-list`,
      { params }
    );
  }

  getCounts(): Observable<CountsResponse> {
    return this.http.get<CountsResponse>(
      `${this.serverUrl}/api/project/v1/counts`
    );
  }

  getPriorities(): Observable<PriorityFilter[]> {
    return this.http.get<PriorityFilter[]>(
      `${this.serverUrl}/api/project/v1/filter/priorities`
    );
  }

  getStages(): Observable<StageFilter[]> {
    return this.http.get<StageFilter[]>(
      `${this.serverUrl}/api/project/v1/filter/stages`
    );
  }

  getMonitoringDates(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.serverUrl}/api/project/v1/filter/Monitoring`
    );
  }

  getProjectDetails(id: number): Observable<any> {
    return this.http.get<any>(
      `${this.serverUrl}/api/project/project/details/${id}`
    );
  }

  getAllProjectDetails() {
    return this.http.get<any>(`${this.serverUrl}/api/project/project-details/list`);
  }

  getVillageList(pid, cid) {
    return this.http.get<any>(`${this.serverUrl}/api/project/village-list/${pid}?cala_id=${cid}`);
  }

  getIndividualVillageDetail(id) {
    return this.http.get<any>(`${this.serverUrl}/api/project/village/${id}`);
  }

  getRequiredVillageDetails(id) {
    this.getIndividualVillageDetail(id).subscribe((res) => {
      this.individualVillageDetail = res;
    });
  }

  postProjectDetails(data) {
    return this.http
      .post<any>(`${this.serverUrl}/api/project/project-details`, data)
      .pipe(catchError((resData) => throwError(resData)));
  }

  retrieveVillageData(id) {
    return this.http.get<any>(`${this.serverUrl}/api/project/village/${id}`);
  }

  updateProjectProgress(data, id) {
    return this.http.put<any>(`${this.serverUrl}/api/data/village/retrieve/${id}`, data);
  }

  getProjectStats(id) {
    return this.http.get<any>(`${this.serverUrl}/api/project/progress/${id}`);
  }

  getCalaStats() {
    return this.http.get<any>(`${this.serverUrl}/api/project/cala`);
  }

  getProjectName() {
    return this.http.get<any>(`${this.serverUrl}/api/project/project-name/list`);
  }

  getStates() {
    return this.http.get<any>(`${this.serverUrl}/api/data/state/list`);
  }

  getDistricts() {
    return this.http.get<any>(`${this.serverUrl}/api/project/v1/filter/districts`);
  }

  getTehsils() {
    return this.http.get<any>(`${this.serverUrl}/api/project/tehsil`);
  }

  getVillages() {
    return this.http.get<any>(`${this.serverUrl}/api/project/village`);
  }

  getAgencies() {
    return this.http.get<any>(`${this.serverUrl}/api/project/v1/filter/agencies`);
  }

  getPrincipal() {
    return this.http.get<any>(`${this.serverUrl}/api/project/principal/list`);
  }

  getComissioners() {
    return this.http.get<any>(`${this.serverUrl}/api/data/comissioner/list`);
  }

  getDistrictCollectors() {
    return this.http.get<any>(`${this.serverUrl}/api/data/district-collector/list`);
  }

  getCala() {
    return this.http.get<any>(`${this.serverUrl}/api/data/cala/list`);
  }

  getExecutiveEngineers() {
    return this.http.get<any>(`${this.serverUrl}/api/project/executive-engineer/list`);
  }

  getRegionalOfficers() {
    return this.http.get<any>(`${this.serverUrl}/api/project/regional-officer/list`);
  }

  getTilr() {
    return this.http.get<any>(`${this.serverUrl}/api/data/tilr/list`);
  }

  getAllProjectList() {
    return this.http.get<any>(`${this.serverUrl}/api/project/list`);
  }

  getProjectWiseStates(id) {
    return this.http.get<any>(`${this.serverUrl}/api/project/state/list/${id}`);
  }

  getStateWiseDistricts(pid, sid) {
    return this.http.get<any>(`${this.serverUrl}/api/project/district/list/${pid}?state=${sid}`);
  }

  getDistrictWiseTehsil(pid, did) {
    return this.http.get<any>(`${this.serverUrl}/api/project/tehsil/list/${pid}?district=${did}`);
  }

  getTehsilWiseVillages(pid, tid) {
    return this.http.get<any>(`${this.serverUrl}/api/project/village/list/${pid}?tehsil=${tid}`);
  }

  get3CData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/3C?village_id=${id}`); }
  get3AData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/3A?village_id=${id}`); }
  getJMRReportData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/v1/jmr_report/list?village_id=${id}`); }
  getJMSData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/jms?village_id=${id}`); }
  get3DData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/3D?village_id=${id}`); }
  get3HData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/3H?village_id=${id}`); }
  get3GData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/3G?village_id=${id}`); }

  getPossessionData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/possession?village_id=${id}`); }
  getMutationData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/mutation?village_id=${id}`); }
  getFundForCalaData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/placing-fund?village_id=${id}`); }
  getGovtLandPossessionData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/govt/possession?village_id=${id}`); }
  getGovtLandMutationData(id) { return this.http.get<any>(`${this.serverUrl}/api/data/govt/mutation?village_id=${id}`); }

  post3CData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/3C`, data); }
  post3AData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/3A`, data); }
  postJMRReportData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/v1/jmr_report/create`, data); }
  postJMSData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/jms`, data); }
  post3DData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/3D`, data); }
  post3GData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/3G`, data); }
  post3HData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/3H`, data); }
  postPossessionata(data) { return this.http.post<any>(`${this.serverUrl}/api/data/possession`, data); }
  postMutationData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/mutation`, data); }
  postGovtLandPossessionData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/govt/possession`, data); }
  postGovtLandMutationData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/govt/mutation`, data); }
  postFundToCala(data) { return this.http.post<any>(`${this.serverUrl}/api/data/placing-fund`, data); }

  updateCompletionStatus(id, data) {
    return this.http.put<any>(`${this.serverUrl}/api/data/village/retrieve/${id}`, data);
  }

  getStateList() { return this.http.get<any>(`${this.serverUrl}/api/data/state/list`); }
  getDistrictList(id) { return this.http.get<any>(`${this.serverUrl}/api/data/district/list/${id}`); }
  getTehsilList(id) { return this.http.get<any>(`${this.serverUrl}/api/data/tehsil/list/${id}`); }
  getVillageListById(id) { return this.http.get<any>(`${this.serverUrl}/api/data/village/list/${id}`); }

  createProject(data) { return this.http.post<any>(`${this.serverUrl}/api/project/project-details`, data); }
  allProjectList() { return this.http.get<any>(`${this.serverUrl}/api/project/project-details/list`); }
  createStateData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/state/detail`, data); }
  createDistrictData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/district/detail`, data); }
  updateDistrictData(data, id) { return this.http.put<any>(`${this.serverUrl}/api/project/districtdata/update/${id}`, data); }
  createTehsilData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/tehsil/detail`, data); }
  updateTehsilData(data, id) { return this.http.put<any>(`${this.serverUrl}/api/project/tehsildata/update/${id}`, data); }
  createVillageData(data) { return this.http.post<any>(`${this.serverUrl}/api/data/village/detail`, data); }

  getDistrictForState(id) { return this.http.get<any>(`${this.serverUrl}/api/data/district/list/${id}`); }
  getTehsilsForDistrict(id) { return this.http.get<any>(`${this.serverUrl}/api/data/tehsil/list/${id}`); }
  getVillagesForTehsil(id) { return this.http.get<any>(`${this.serverUrl}/api/data/village/list/${id}`); }

  postPNactualarea(data) { return this.http.post<any>(`${this.serverUrl}/api/data/pn/area-aquired`, data); }
  getPNactualarea(id) { return this.http.get<any>(`${this.serverUrl}/api/data/pn/area-aquired?village_id=${id}`); }
  retrivePNactualarea(id) { return this.http.get<any>(`${this.serverUrl}/api/data/pn/area-aquired/retrieve-update/${id}`); }
  updatePNactualarea(id, data) { return this.http.put<any>(`${this.serverUrl}/api/data/pn/area-aquired/retrieve-update/${id}`, data); }
  deletePNactualarea(id) { return this.http.delete<any>(`${this.serverUrl}/api/data/pn/area-aquired/retrieve-update/${id}`); }

  postFundDeposition(data) { return this.http.post<any>(`${this.serverUrl}/api/data/pn/fund-deposition`, data); }
  getFundDeposition(id) { return this.http.get<any>(`${this.serverUrl}/api/data/pn/fund-deposition?village_id=${id}`); }
  retrivefundDeposite(id) { return this.http.get<any>(`${this.serverUrl}/api/data/pn/fund-deposition/retrieve-update/${id}`); } 
  updatefundDeposite(id, data) { return this.http.put<any>(`${this.serverUrl}/api/data/pn/fund-deposition/retrieve-update/${id}`, data); }
  deletefundDeposite(id) { return this.http.delete<any>(`${this.serverUrl}/api/data/pn/fund-deposition/retrieve-update/${id}`); }

  postFundpaid(data) { return this.http.post<any>(`${this.serverUrl}/api/data/pn/fund-distribution`, data); }
  getFundpaid(id) { return this.http.get<any>(`${this.serverUrl}/api/data/pn/fund-distribution?village_id=${id}`); }
  retrivefundpaid(id) { return this.http.get<any>(`${this.serverUrl}/api/data/pn/fund-distribution/retrieve-update/${id}`); }
  updatefundpaid(id, data) { return this.http.put<any>(`${this.serverUrl}/api/data/pn/fund-distribution/retrieve-update/${id}`, data); }
  deletefundpaid(id) { return this.http.delete<any>(`${this.serverUrl}/api/data/pn/fund-distribution/retrieve-update/${id}`); }

  retriveVilldetails(id) { return this.http.get<any>(`${this.serverUrl}/api/data/village/retrieve/${id}`); }
}
